import * as React from 'react'
import type { Root } from 'react-markdown/lib/react-markdown'

import styles from './StreamingText.module.scss'

const getId = (node: Root) => [node.position?.start?.line, node.position?.start?.offset].join('-')

type Props = {
  readonly children: React.ReactNode
  readonly node: Root
  readonly visitedNodes: Map<string, string>
  readonly render: (children: React.ReactNode) => React.ReactNode
}

/**
 * Handles streaming text rendering with fade-in effects while tracking previously rendered content.
 * Ensures that:
 * - Non-string and multiple children cases are rendered normally without fade-in.
 * - Previously rendered content is stored and used for transitions.
 * @param  props - The props
 * @param  props.children - The default children to render
 * @param  props.node - The current node to get the id from
 * @param  props.visitedNodes - The visited nodes Map, to store the rendered text
 * @param  props.render - The original render function
 * @returns The rendered element
 */
export function StreamingText({ children, node, visitedNodes, render }: Props) {
  const id = getId(node)
  const preText = visitedNodes.get(id) || ''

  // Early return if children length is invalid for fade-in
  if (Array.isArray(children) && children.length !== 1) {
    return render(children)
  }

  const content = Array.isArray(children) ? children[0] : children
  if (typeof content !== 'string') {
    // non-string content does not support fade in
    return render(children)
  }

  // Store the latest rendered content to track updates
  visitedNodes.set(id, content)

  if (!preText) {
    // First-time rendering: apply fade-in effect
    return render(<span className={styles.fadeIn}>{content}</span>)
  }

  /**
   * If content starts with preText and has changed, render the new part with fade-in.
   * This ensures smooth transitions instead of abrupt text changes.
   */
  if (content.startsWith(preText) && preText !== content) {
    const fadeInContent = content.slice(preText.length)
    return render(
      <>
        {preText}
        <span className={styles.fadeIn}>{fadeInContent}</span>
      </>,
    )
  }

  // If nothing changes, render the children as-is
  return render(children)
}
